/** /host — list of sessions; /host/new — create one. */
import { formatDateTime, LOCALES } from "@shared/i18n";
import { DEFAULT_SETTINGS, type SessionRecord, type SessionSettings } from "@shared/protocol";
import type { Scenario } from "@shared/scenario";
import { SIMULATION_TEMPERAMENTS, type SimulationTemperament } from "@shared/simulation";
import { useEffect, useState } from "preact/hooks";
import { Header, Page } from "../components/Header";
import { Icon } from "../components/Icon";
import { Button, Card, Field, Pill, Spinner, Toggle } from "../components/ui";
import { useI18n } from "../i18n";
import { type ApiError, get, post } from "../lib/api";
import { type CatalogueEntry, forkScenario, listScenarios } from "../lib/scenario";
import { Link, navigate } from "../router";

export interface FacilitatorInfo {
  id: string;
  email: string;
  name: string | null;
}

interface SessionsResponse {
  sessions: (SessionRecord & { links: Record<string, string> })[];
}

export function HostNav({
  facilitator,
  onSignOut,
  section,
}: {
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
  /** Which area the page belongs to; the matching link reads as current. */
  section?: "sessions" | "build";
}) {
  const { t } = useI18n();
  return (
    <Header
      right={
        <>
          <Link href="/host" class="btn ghost sm" aria-current={section === "sessions" ? "page" : undefined}>
            {t("host.nav.sessions")}
          </Link>
          <Link href="/build" class="btn ghost sm" aria-current={section === "build" ? "page" : undefined}>
            {t("host.nav.build")}
          </Link>
          <span
            class="muted small nowrap"
            style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {facilitator.email}
          </span>
          <Button variant="ghost" size="sm" icon="log-out" onClick={onSignOut}>
            <span class="visually-hidden">{t("action.signOut")}</span>
          </Button>
        </>
      }
    />
  );
}

export function HostDashboard({
  facilitator,
  onSignOut,
}: {
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
}) {
  const { t, locale } = useI18n();
  const [data, setData] = useState<SessionsResponse | null>(null);
  useEffect(() => {
    get<SessionsResponse>("/api/sessions")
      .then(setData)
      .catch(() => setData({ sessions: [] }));
  }, []);
  return (
    <Page>
      <HostNav facilitator={facilitator} onSignOut={onSignOut} section="sessions" />
      <main id="main" class="stack" style={{ paddingBlock: "var(--s-4) var(--s-12)" }}>
        <div class="row between">
          <h1 style={{ margin: 0 }}>{t("host.dash.title")}</h1>
          <Button icon="plus" onClick={() => navigate("/host/new")}>
            {t("host.dash.new")}
          </Button>
        </div>
        {!data ? (
          <Spinner />
        ) : data.sessions.length === 0 ? (
          <div class="empty">{t("host.dash.empty")}</div>
        ) : (
          <div class="grid grid-2">
            {data.sessions.map((s) => (
              <Card key={s.id} class="tight stack-sm">
                <div class="row between">
                  <Pill tone={s.status === "live" ? "live" : s.status === "ended" ? undefined : "accent"}>
                    {t(`status.${s.status}`)}
                  </Pill>
                  <span class="code-display fg-2">{s.code}</span>
                </div>
                <h3 style={{ margin: 0 }}>{s.title}</h3>
                <p class="muted small" style={{ margin: 0 }}>
                  {t("host.dash.created", { when: formatDateTime(locale, s.createdAt) })} ·{" "}
                  {t("host.dash.players", { count: s.playerCount })}
                  {s.readiness !== null ? ` · ${t("host.dash.readiness", { score: s.readiness })}` : ""}
                </p>
                <div class="row">
                  <Link href={`/host/sessions/${s.id}`} class="btn sm">
                    {t("host.dash.openConsole")}
                  </Link>
                  {s.status === "ended" || s.status === "debrief" ? (
                    <a href={s.links.report} class="btn secondary sm" target="_blank" rel="noopener">
                      <Icon name="file-text" /> {t("host.dash.report")}
                    </a>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </Page>
  );
}

export function HostNew({
  facilitator,
  onSignOut,
  query,
}: {
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
  /** `?scenario=<id>` preselects one (a shared link, or the editor's "run a test session"). */
  query?: URLSearchParams;
}) {
  const { t, locale } = useI18n();
  const wanted = query?.get("scenario") ?? null;
  const [scenarios, setScenarios] = useState<CatalogueEntry[] | null>(null);
  const [scenarioId, setScenarioId] = useState<string | null>(wanted);
  const [useDraft, setUseDraft] = useState(query?.get("draft") === "1");
  const [forking, setForking] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [settings, setSettings] = useState<SessionSettings>({ ...DEFAULT_SETTINGS, locale });
  const [demo, setDemo] = useState(false);
  const [demoCount, setDemoCount] = useState(12);
  const [demoTemperament, setDemoTemperament] = useState<SimulationTemperament>("mixed");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listScenarios(locale).then(async (r) => {
      let list = r.scenarios;
      // A link to an unlisted scenario names one the list does not carry; fetch it on its own.
      if (wanted && !list.some((s) => s.id === wanted)) {
        const summary = await summaryFromFull(wanted, locale);
        if (summary) list = [...list, summary];
      }
      setScenarios(list);
      setScenarioId((cur) => (cur && list.some((s) => s.id === cur) ? cur : (list[0]?.id ?? null)));
    });
  }, [locale, wanted]);

  const chosen = scenarios?.find((s) => s.id === scenarioId) ?? null;
  const chosenIsDraftOnly = chosen?.origin === "mine" && !chosen.publishedVersion;

  const create = async () => {
    if (!scenarioId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await post<{ session: SessionRecord }>("/api/sessions", {
        scenarioId,
        useDraft: chosen?.origin === "mine" ? chosenIsDraftOnly || useDraft : undefined,
        title: title.trim() || undefined,
        settings,
        simulate: demo ? { count: demoCount, temperament: demoTemperament, pace: "natural" } : undefined,
      });
      navigate(`/host/sessions/${res.session.id}`);
    } catch (e) {
      setError((e as ApiError).message);
      setBusy(false);
    }
  };

  const copyAndEdit = async (id: string) => {
    setForking(id);
    setError(null);
    try {
      const res = await forkScenario(id);
      navigate(`/build/${res.scenario.id}`);
    } catch (e) {
      setError((e as ApiError).message);
      setForking(null);
    }
  };

  const groups: { key: string; label: string; who: string | null; items: CatalogueEntry[] }[] = [];
  if (scenarios) {
    for (const audience of ["workplace", "community", "youth"] as const) {
      const items = scenarios.filter(
        (s) => s.origin === "catalogue" && (s.audience ?? "workplace") === audience,
      );
      if (items.length)
        groups.push({
          key: audience,
          label: t(`audience.${audience}`),
          who: t(`audience.${audience}.who`),
          items,
        });
    }
    const mine = scenarios.filter((s) => s.origin === "mine");
    if (mine.length)
      groups.push({
        key: "mine",
        label: t("host.new.group.mine"),
        who: t("host.new.group.mine.who"),
        items: mine,
      });
    const community = scenarios.filter((s) => s.origin === "community");
    if (community.length) {
      groups.push({
        key: "community",
        label: t("host.new.group.community"),
        who: t("host.new.group.community.who"),
        items: community,
      });
    }
    const shared = scenarios.filter((s) => s.origin === "shared");
    if (shared.length)
      groups.push({ key: "shared", label: t("host.new.group.shared"), who: null, items: shared });
  }

  return (
    <Page>
      <HostNav facilitator={facilitator} onSignOut={onSignOut} section="sessions" />
      <main id="main" class="stack" style={{ paddingBlock: "var(--s-4) var(--s-12)" }}>
        <div class="row">
          <Button variant="ghost" size="sm" icon="arrow-left" onClick={() => navigate("/host")}>
            {t("action.back")}
          </Button>
        </div>
        <h1 style={{ margin: 0 }}>{t("host.new.title")}</h1>
        {!scenarios ? (
          <Spinner />
        ) : (
          <div class="console">
            <section class="stack">
              <h2 style={{ fontSize: "var(--text-lg)", margin: 0 }}>{t("host.new.pickScenario")}</h2>
              <div class="choice-list" role="radiogroup" aria-label={t("host.new.pickScenario")}>
                {groups.map((group) => (
                  <div key={group.key} class="choice-group">
                    <div class="choice-group-head">
                      <span class="eyebrow">{group.label}</span>
                      {group.who ? <span class="muted xs">{group.who}</span> : null}
                    </div>
                    {group.items.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        role="radio"
                        class="choice"
                        aria-checked={scenarioId === s.id}
                        onClick={() => setScenarioId(s.id)}
                        style={{ display: "block" }}
                      >
                        <div class="row between">
                          <span class="title">{s.title}</span>
                          <span class="row">
                            {s.origin === "mine" ? (
                              <Pill tone={s.status === "published" ? "accent" : undefined}>
                                {s.status === "published"
                                  ? s.unpublishedChanges
                                    ? t("build.status.publishedChanges")
                                    : t("build.status.published")
                                  : t("build.status.draft")}
                              </Pill>
                            ) : null}
                            <Pill tone="signal">{t(`scenario.difficulty.${s.difficulty}`)}</Pill>
                            <Pill>{t("scenario.minutes", { count: s.estimatedMinutes })}</Pill>
                          </span>
                        </div>
                        <div class="sub">{s.tagline}</div>
                        <div class="sub">
                          {t("scenario.roles", { count: s.roles.length })} ·{" "}
                          {t("scenario.decisions", { count: s.decisions })} ·{" "}
                          {t("scenario.injects", { count: s.injects })}
                          {s.audienceNote ? <> · {s.audienceNote}</> : null}
                          {s.sourceTitle ? <> · {t("build.basedOn", { title: s.sourceTitle })}</> : null}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              {chosen ? (
                <Card class="tight stack-sm">
                  <p class="fg-2">{chosen.summary}</p>
                  <strong class="small">{t("scenario.objectives")}</strong>
                  <ul class="small">
                    {chosen.learningObjectives.map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                  {chosen.basedOn.length ? (
                    <p class="muted xs">
                      {t("scenario.basedOn")}:{" "}
                      {chosen.basedOn.map((b, i) => (
                        <span key={b.name}>
                          {i ? ", " : ""}
                          {b.url ? (
                            <a href={b.url} target="_blank" rel="noopener">
                              {b.name} ({b.year})
                            </a>
                          ) : (
                            `${b.name} (${b.year})`
                          )}
                        </span>
                      ))}
                    </p>
                  ) : null}
                  <div class="row xs muted">
                    {Object.entries(chosen.translations).map(([tag, cov]) => (
                      <span key={tag} class="pill">
                        {tag} {cov < 1 ? `${Math.round(cov * 100)}%` : ""}
                      </span>
                    ))}
                  </div>
                  <div class="row">
                    {chosen.origin === "mine" ? (
                      <Link href={`/build/${chosen.id}`} class="btn secondary sm">
                        <Icon name="settings" /> {t("build.open")}
                      </Link>
                    ) : null}
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="copy"
                      disabled={forking !== null}
                      onClick={() => copyAndEdit(chosen.id)}
                    >
                      {forking === chosen.id ? t("build.copying") : t("build.copyAndEdit")}
                    </Button>
                  </div>
                  {chosen.origin === "mine" && chosen.publishedVersion && chosen.unpublishedChanges ? (
                    <Toggle
                      label={t("host.new.useDraft")}
                      help={t("host.new.useDraft.help", { version: chosen.publishedVersion })}
                      checked={useDraft}
                      onChange={setUseDraft}
                    />
                  ) : null}
                </Card>
              ) : null}
            </section>
            <section class="stack">
              <Card class="stack">
                <Field
                  label={t("host.new.sessionTitle")}
                  help={t("host.new.sessionTitleHelp")}
                  htmlFor="title"
                >
                  <input
                    id="title"
                    class="input"
                    value={title}
                    maxLength={120}
                    placeholder={chosen?.title ?? ""}
                    onInput={(e) => setTitle((e.currentTarget as HTMLInputElement).value)}
                  />
                </Field>
                <h2 style={{ fontSize: "var(--text-lg)", margin: 0 }}>{t("host.new.settings")}</h2>
                <SettingsForm
                  settings={settings}
                  onChange={(patch) => setSettings((s) => ({ ...s, ...patch }))}
                />
                <hr />
                <Toggle
                  label={t("host.new.demo")}
                  help={t("host.new.demo.help")}
                  checked={demo}
                  onChange={setDemo}
                />
                {demo ? (
                  <div class="sim-fields">
                    <Field label={t("host.sim.count")} htmlFor="demo-count">
                      <input
                        id="demo-count"
                        class="input"
                        type="number"
                        min={1}
                        max={40}
                        value={demoCount}
                        onInput={(e) =>
                          setDemoCount(Number((e.currentTarget as HTMLInputElement).value) || 1)
                        }
                      />
                    </Field>
                    <Field label={t("host.sim.temperament")} htmlFor="demo-temperament">
                      <select
                        id="demo-temperament"
                        class="input"
                        value={demoTemperament}
                        onChange={(e) =>
                          setDemoTemperament(
                            (e.currentTarget as HTMLSelectElement).value as SimulationTemperament,
                          )
                        }
                      >
                        {SIMULATION_TEMPERAMENTS.map((x) => (
                          <option key={x} value={x}>
                            {t(`host.sim.temperament.${x}`)}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                ) : null}
                {error ? (
                  <p role="alert" class="error-text">
                    {error}
                  </p>
                ) : null}
                <Button size="lg" block disabled={busy || !scenarioId} icon="play" onClick={create}>
                  {busy ? t("host.new.creating") : t("host.new.create")}
                </Button>
              </Card>
            </section>
          </div>
        )}
      </main>
    </Page>
  );
}

export function SettingsForm({
  settings,
  onChange,
}: {
  settings: SessionSettings;
  onChange: (patch: Partial<SessionSettings>) => void;
}) {
  const { t } = useI18n();
  return (
    <div class="stack-sm">
      <Toggle
        label={t("host.settings.liveTally")}
        help={t("host.settings.liveTally.help")}
        checked={settings.liveTally}
        onChange={(liveTally) => onChange({ liveTally })}
      />
      <Toggle
        label={t("host.settings.allowViewers")}
        help={t("host.settings.allowViewers.help")}
        checked={settings.allowViewers}
        onChange={(allowViewers) => onChange({ allowViewers })}
      />
      <Toggle
        label={t("host.settings.openRoleSelection")}
        help={t("host.settings.openRoleSelection.help")}
        checked={settings.openRoleSelection}
        onChange={(openRoleSelection) => onChange({ openRoleSelection })}
      />
      <Toggle
        label={t("host.settings.showLeaderboard")}
        checked={settings.showLeaderboard}
        onChange={(showLeaderboard) => onChange({ showLeaderboard })}
      />
      <Toggle
        label={t("host.settings.showSidebar")}
        help={t("host.settings.showSidebar.help")}
        checked={settings.showSidebar}
        onChange={(showSidebar) => onChange({ showSidebar })}
      />
      <Toggle
        label={t("host.settings.reactions")}
        checked={settings.reactions}
        onChange={(reactions) => onChange({ reactions })}
      />
      <Field label={t("host.settings.autoAdvance")} help={t("host.settings.autoAdvance.help")} htmlFor="auto">
        <input
          id="auto"
          class="input"
          type="number"
          min={0}
          max={600}
          step={5}
          value={settings.autoAdvanceSec}
          onInput={(e) =>
            onChange({ autoAdvanceSec: Number((e.currentTarget as HTMLInputElement).value) || 0 })
          }
        />
      </Field>
      <Field label={t("host.settings.locale")} help={t("host.settings.locale.help")} htmlFor="clocale">
        <select
          id="clocale"
          class="input"
          value={settings.locale}
          onChange={(e) => onChange({ locale: (e.currentTarget as HTMLSelectElement).value })}
        >
          {LOCALES.map((l) => (
            <option key={l.tag} value={l.tag} lang={l.tag}>
              {l.name}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

/**
 * A summary for a scenario the list does not carry (an unlisted one reached by link), built from
 * the full document the way the Worker builds catalogue summaries.
 */
async function summaryFromFull(id: string, locale: string): Promise<CatalogueEntry | null> {
  const res = await get<{ scenario: Scenario }>(
    `/api/scenarios/${encodeURIComponent(id)}?lang=${encodeURIComponent(locale)}`,
  ).catch(() => null);
  if (!res) return null;
  const s = res.scenario;
  return {
    id: s.id,
    version: s.version,
    title: s.title,
    tagline: s.tagline,
    summary: s.summary,
    difficulty: s.difficulty,
    estimatedMinutes: s.estimatedMinutes,
    audience: s.audience,
    ...(s.audienceNote ? { audienceNote: s.audienceNote } : {}),
    tags: s.tags,
    roles: s.roles.map((r) => ({ id: r.id, name: r.name, icon: r.icon })),
    decisions: Object.values(s.nodes).filter((n) => n.type === "decision").length,
    injects: s.injects.length,
    basedOn: s.basedOn,
    learningObjectives: s.learningObjectives,
    translations: { en: 1 },
    origin: "shared",
  };
}
